import React, { useState, useEffect } from 'react';
import { Container, Typography, List, Divider, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getUserTransactions } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import BookingItem from '../components/BookingItem';

function LendingCompletedBookings() {
    const { currentUser, loading } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && currentUser) {
            const fetchBookings = async () => {
                try {
                    setIsLoading(true);
                    const response = await getUserTransactions();
                    const lenderBookings = response.data.filter(
                        (booking) => booking.lender_id === currentUser.id
                    );

                    const completedBookings = lenderBookings.filter((booking) =>
                        ['completed'].includes(booking.status.toLowerCase())
                    );

                    setBookings(completedBookings);
                    setError(null);
                } catch (error) {
                    console.error('Error fetching bookings:', error);
                    setError('Failed to load bookings. Please try again later.');
                } finally {
                    setIsLoading(false);
                }
            };

            fetchBookings();
        }
    }, [loading, currentUser]);

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Completed Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    Loading your completed bookings...
                </Typography>
            </Container>
        );
    }

    if (!currentUser) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Completed Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    You need to be logged in to view your completed bookings.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    Back
                </Button>
                <Typography variant="h4" component="h1" flexGrow={1}>
                    Completed Lending Bookings
                </Typography>
            </Box>

            {error && (
                <Typography color="error" paragraph>
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Typography variant="body1" paragraph>
                    Loading your completed bookings...
                </Typography>
            ) : bookings.length === 0 ? (
                <Typography variant="body1" paragraph>
                    You don't have any completed bookings yet.
                </Typography>
            ) : (
                <List>
                    {bookings.map((booking) => (
                        <React.Fragment key={booking.id}>
                            <BookingItem
                                booking={booking}
                                showActions={false}
                            />
                            <Divider variant="inset" component="li" />
                        </React.Fragment>
                    ))}
                </List>
            )}
        </Container>
    );
}

export default LendingCompletedBookings;
